import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import './Login.css';
 
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); // optional

  // Step 2 of login: OTP emailed after a correct username/password.
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpTimeLeft, setOtpTimeLeft] = useState(600); // 10 minutes, matches backend OTP expiry
  const [resending, setResending] = useState(false);

  const { login, verifyLoginOtp } = useAuth();
  const navigate = useNavigate();

  const finishLogin = () => {
    // Persist username + password when Remember Me is checked
    // SECURITY: Storing raw passwords in localStorage is insecure. Only enable if you accept the risk.
    try {
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', username || '');
        localStorage.setItem('rememberedPassword', password || '');
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }
    } catch (err) {
      console.warn('Could not persist remember-me credentials', err);
    }
    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password, rememberMe);
      if (result.success && result.requiresOtp) {
        setOtpEmail(result.email_id);
        setOtp('');
        setOtpTimeLeft(600);
        setStep('otp');
      } else if (result.success) {
        finishLogin();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyLoginOtp(otpEmail, otp, rememberMe);
      if (result.success) {
        finishLogin();
      } else {
        setError(result.error || 'Invalid or expired code');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setResending(true);
    try {
      const result = await login(username, password, rememberMe);
      if (result.success && result.requiresOtp) {
        setOtp('');
        setOtpTimeLeft(600);
      } else {
        setError(result.error || 'Could not resend code');
      }
    } catch (err) {
      setError('Could not resend code');
    } finally {
      setResending(false);
    }
  };

  // Countdown for the OTP step
  useEffect(() => {
    if (step !== 'otp' || otpTimeLeft <= 0) return;
    const timer = setInterval(() => setOtpTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [step, otpTimeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Load remembered credentials on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('rememberMe');
      if (saved === 'true') {
        const ru = localStorage.getItem('rememberedUsername') || '';
        const rp = localStorage.getItem('rememberedPassword') || '';
        setUsername(ru);
        setPassword(rp);
        setRememberMe(true);
      }
    } catch (err) {
      console.warn('Error reading remember-me from storage', err);
    }
  }, []);
 
  return (
    <div className="login-container">
      <div className="login-split-box">
        {/* LEFT IMAGE */}
        <div className="login-image-side">
          <img
            src="/assets/img/time1.png"
            alt="Login Visual"
            className="login-side-image"
          />
        </div>
 
        {/* RIGHT LOGIN CARD */}
        <div className="login-card">
          <div className="login-card-content">
            <div className="login-logo">
              <img src="/assets/img/logo1.png" alt="BuildINT" />
            </div>
 
            <h2 className="login-title">
              Welcome to <span className="brand-highlight">BTS</span>
            </h2>
            <p className="login-subtitle">
              {step === 'credentials' ? 'Sign in to your account' : 'Enter the verification code we emailed you'}
            </p>

            {/* Error message with aria-live */}
            {error && (
              <div className="alert alert-error" role="alert" aria-live="assertive">
                {error}
              </div>
            )}

            {step === 'credentials' ? (
              <form onSubmit={handleSubmit} noValidate>
                {/* USERNAME FIELD WITH ICON */}
                <div className="input-field">
                  <label htmlFor="username" className="input-label">
                    Username
                  </label>
                  <div className="input-wrapper">
                    <i className="bi bi-person input-icon"></i>
                    <input
                      type="text"
                      id="username"
                      className="form-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                      required
                      placeholder="Enter your username"
                    />
                  </div>
                </div>

                {/* PASSWORD FIELD WITH TOGGLE */}
                <div className="input-field">
                  <label htmlFor="password" className="input-label">
                    Password
                  </label>
                  <div className="input-wrapper">
                    <i className="bi bi-lock input-icon"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className="form-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      disabled={loading}
                    >
                      <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                    </button>
                  </div>
                </div>

                {/* OPTIONS ROW */}
                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>

                {/* SUBMIT BUTTON WITH LOADING STATE */}
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner" aria-hidden="true"></span>
                      Logging in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} noValidate>
                <div className="input-field">
                  <label htmlFor="login-otp" className="input-label">
                    Verification Code
                  </label>
                  <div className="input-wrapper">
                    <i className="bi bi-shield-lock input-icon"></i>
                    <input
                      type="text"
                      id="login-otp"
                      className="form-input"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={loading}
                      required
                      maxLength="6"
                      placeholder="Enter 6-digit code"
                      autoFocus
                    />
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <small style={{ color: '#666' }}>Sent to {otpEmail}</small>
                    <small style={{ color: otpTimeLeft < 120 ? '#ff6b6b' : '#999' }}>
                      ⏱️ {formatTime(otpTimeLeft)}
                    </small>
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading || otp.length !== 6 || otpTimeLeft <= 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner" aria-hidden="true"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify & Sign in'
                  )}
                </button>

                <div className="form-options" style={{ justifyContent: 'space-between', marginTop: '14px' }}>
                  <button
                    type="button"
                    className="forgot-link"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={handleResendOtp}
                    disabled={resending || loading}
                  >
                    {resending ? 'Resending…' : 'Resend code'}
                  </button>
                  <button
                    type="button"
                    className="forgot-link"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => { setStep('credentials'); setOtp(''); setError(''); }}
                    disabled={loading}
                  >
                    Use a different account
                  </button>
                </div>
              </form>
            )}

            {/* FOOTER LINKS */}
            <div className="login-footer">
              <div className="version">Version 1.2.7</div>
              <div className="copyright">
                © Copyright 2026 BuildINT. All Rights Reserved&nbsp; | &nbsp;Designed by BuildINT
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default Login;
 